/**
 * Secure Logging Utility
 * Provides controlled logging with sensitive data protection
 */

/**
 * Log levels
 */
export const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

/**
 * Sensitive data patterns to redact
 */
const SENSITIVE_PATTERNS = [
  // Passwords
  /password[^a-zA-Z0-9]*[:=][^,}\]]+/gi,
  // Tokens and keys
  /(token|key|secret|apikey)[^a-zA-Z0-9]*[:=][^,}\]]+/gi,
  // Email patterns in sensitive contexts
  /email[^a-zA-Z0-9]*[:=][^,}\]]+/gi,
  // Phone numbers
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  // Credit card numbers
  /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
  // Social security numbers
  /\b\d{3}-\d{2}-\d{4}\b/g
];

/**
 * Fields that should always be redacted
 */
const SENSITIVE_FIELDS = [
  'password',
  'adminPassword',
  'token',
  'accessToken',
  'refreshToken',
  'secret',
  'privateKey',
  'apiKey',
  'sessionId',
  'creditCard',
  'ssn',
  'socialSecurityNumber'
];

/**
 * Production logging configuration
 */
const PRODUCTION_CONFIG = {
  enableDebug: false,
  enableInfo: true,
  enableWarn: true,
  enableError: true,
  maxLogLength: 1000,
  sensitiveDataRedaction: true
};

/**
 * Development logging configuration
 */
const DEVELOPMENT_CONFIG = {
  enableDebug: true,
  enableInfo: true,
  enableWarn: true,
  enableError: true,
  maxLogLength: 5000,
  sensitiveDataRedaction: true // Still redact in development for safety
};

/**
 * Get current logging configuration based on environment
 */
const getConfig = () => {
  return process.env.NODE_ENV === 'production' ? PRODUCTION_CONFIG : DEVELOPMENT_CONFIG;
};

/**
 * Redacts sensitive information from data
 * @param {any} data - The data to sanitize
 * @returns {any} - The sanitized data
 */
const redactSensitiveData = (data) => {
  if (!data) return data;
  
  try {
    // Convert to string for processing
    let str = typeof data === 'string' ? data : JSON.stringify(data);
    
    // Apply sensitive pattern replacements
    SENSITIVE_PATTERNS.forEach(pattern => {
      str = str.replace(pattern, (match) => {
        const parts = match.split(/[:=]/);
        return parts.length > 1 ? `${parts[0]}:[REDACTED]` : '[REDACTED]';
      });
    });
    
    // Try to parse back to object if it was originally an object
    if (typeof data === 'object') {
      try {
        const parsed = JSON.parse(str);
        return redactObjectFields(parsed);
      } catch {
        return str;
      }
    }
    
    return str;
  } catch (error) {
    return '[REDACTION_ERROR]';
  }
};

/**
 * Redacts sensitive fields from object
 * @param {Object} obj - The object to sanitize
 * @returns {Object} - The sanitized object
 */
const redactObjectFields = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const sanitized = Array.isArray(obj) ? [] : {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (SENSITIVE_FIELDS.some(field => 
      key.toLowerCase().includes(field.toLowerCase())
    )) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object') {
      sanitized[key] = redactObjectFields(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Truncates log message if too long
 * @param {string} message - The message to truncate
 * @param {number} maxLength - Maximum length allowed
 * @returns {string} - The truncated message
 */
const truncateMessage = (message, maxLength) => {
  if (!message || message.length <= maxLength) return message;
  return message.substring(0, maxLength) + '... [TRUNCATED]';
};

/**
 * Formats log entry
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {any} data - Additional data
 * @returns {Object} - Formatted log entry
 */
const formatLogEntry = (level, message, data = null) => {
  const config = getConfig();
  
  let sanitizedData = data;
  if (config.sensitiveDataRedaction && data) {
    sanitizedData = redactSensitiveData(data);
  }
  
  return {
    timestamp: new Date().toISOString(),
    level,
    message: truncateMessage(message, config.maxLogLength),
    ...(sanitizedData && { data: sanitizedData }),
    environment: process.env.NODE_ENV || 'development'
  };
};

/**
 * Secure logger class
 */
class SecureLogger {
  constructor() {
    this.config = getConfig();
  }

  /**
   * Log error message
   * @param {string} message - Error message
   * @param {any} data - Additional data
   */
  error(message, data = null) {
    if (!this.config.enableError) return;
    
    const logEntry = formatLogEntry(LOG_LEVELS.ERROR, message, data);
    
    if (process.env.NODE_ENV === 'production') {
      // In production, send to monitoring service
      this.sendToMonitoring(logEntry);
    } else {
      console.error('[SECURE_LOG]', logEntry);
    }
  }

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {any} data - Additional data
   */
  warn(message, data = null) {
    if (!this.config.enableWarn) return;
    
    const logEntry = formatLogEntry(LOG_LEVELS.WARN, message, data);
    
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(logEntry);
    } else {
      console.warn('[SECURE_LOG]', logEntry);
    }
  }

  /**
   * Log info message
   * @param {string} message - Info message
   * @param {any} data - Additional data
   */
  info(message, data = null) {
    if (!this.config.enableInfo) return;
    
    const logEntry = formatLogEntry(LOG_LEVELS.INFO, message, data);
    
    if (process.env.NODE_ENV === 'development') {
      console.info('[SECURE_LOG]', logEntry);
    }
    // Info logs not sent to monitoring in production to reduce noise
  }

  /**
   * Log debug message
   * @param {string} message - Debug message
   * @param {any} data - Additional data
   */
  debug(message, data = null) {
    if (!this.config.enableDebug) return;
    
    const logEntry = formatLogEntry(LOG_LEVELS.DEBUG, message, data);
    
    if (process.env.NODE_ENV === 'development') {
      console.debug('[SECURE_LOG]', logEntry);
    }
    // Debug logs never sent to monitoring
  }

  /**
   * Send log entry to monitoring service (placeholder)
   * @param {Object} logEntry - The log entry to send
   */
  sendToMonitoring(logEntry) {
    // TODO: Implement actual monitoring service integration
    // For now, just use console in production but with sanitized data
    console.log('[MONITORING]', logEntry);
  }

  /**
   * Log authentication events
   * @param {string} event - Event type
   * @param {Object} details - Event details
   */
  logAuth(event, details = {}) {
    this.info(`Authentication event: ${event}`, {
      event,
      timestamp: new Date().toISOString(),
      userId: details.userId,
      userAgent: details.userAgent,
      ip: details.ip,
      // Don't log sensitive auth details
      success: details.success
    });
  }

  /**
   * Log security events
   * @param {string} event - Security event type
   * @param {Object} details - Event details
   */
  logSecurity(event, details = {}) {
    this.warn(`Security event: ${event}`, {
      event,
      timestamp: new Date().toISOString(),
      severity: details.severity || 'medium',
      userId: details.userId,
      ip: details.ip,
      userAgent: details.userAgent,
      details: redactSensitiveData(details.details)
    });
  }

  /**
   * Log performance metrics
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   * @param {Object} metadata - Additional metadata
   */
  logPerformance(operation, duration, metadata = {}) {
    if (duration > 1000) { // Only log slow operations
      this.warn(`Slow operation detected: ${operation}`, {
        operation,
        duration,
        timestamp: new Date().toISOString(),
        ...redactSensitiveData(metadata)
      });
    }
  }
}

// Create singleton instance
const logger = new SecureLogger();

export default logger;

// Export convenience methods
export const logError = (message, data) => logger.error(message, data);
export const logWarn = (message, data) => logger.warn(message, data);
export const logInfo = (message, data) => logger.info(message, data);
export const logDebug = (message, data) => logger.debug(message, data);
export const logAuth = (event, details) => logger.logAuth(event, details);
export const logSecurity = (event, details) => logger.logSecurity(event, details);
export const logPerformance = (operation, duration, metadata) => 
  logger.logPerformance(operation, duration, metadata);